import { useState, useContext, useEffect, useRef } from 'react';
import { makeStyles } from '@material-ui/styles';
import { Button, FormGroup, FormLabel, TextField, Grid, Collapse, Divider } from '@material-ui/core';
import Autocomplete from '@material-ui/lab/Autocomplete';

import AuthContext from '../../context';
import { get, post } from '../../utils/api-services';
import helpers from '../../utils/helpers';
import themeColors from '../../assets/colors';

const useStyles = makeStyles(() => ({
    buttonContainer: {
        display: 'flex',
        flexWrap: 'wrap',
        justifyContent: 'center',
        alignItems: 'center',
        marginBottom: 5
    },
    storeBtn: {
        width: '100%',
        ...themeColors.blueBtn
    }
}));

const defSede = { id: 0, name: 'Choose a sede' };

const RecipeCopyRecipe = ({ open, onClose, id, permissions, updateRecipe }) => {

    const sede = JSON.parse(localStorage.getItem('@sede'));
    const classes = useStyles();
    const { showToast, signOut, showLoader } = useContext(AuthContext);
    const isMounted = useRef(false);
    const [state, setState] = useState({
        sedes: [defSede],
        sede: defSede
    });

    useEffect(() => {
        if (open && !isMounted.current) {
            makeRequest();
            isMounted.current = true;
        }
    }, [open]);

    const makeRequest = async () => {
        showLoader(true);
        const response = await get('/recipe/getSedes');
        if (response.status === 200) {
            setState(prev => ({ ...prev, sedes: response.data }));
        } else {
            console.log('[RecipeCopyRecipe] makeRequest error', response);
            helpers.failRequest(response, showToast, signOut);
        }
        showLoader(false);
    }

    const handleChange = (e, value) => {
        if (value?.id === sede?.id) {
            showToast('You cannot select your own sede', 'error');
            setState(prev => ({ ...prev, sede: defSede }));
        } else {
            setState(prev => ({ ...prev, sede: value }))
        }
    }

    const validate = () => {
        const { sede } = state;
        if (!Number(sede.id)) {
            showToast('Sede field is required', 'warning');
            return false;
        }

        return window.confirm(`Are you sure you want to copy the "${sede.name}" recipe? \nThis action cannot be reversed!`);
    }

    const copyRecipe = async () => {
        const cond = validate();

        if (cond) {
            showLoader(true);
            const response = await post(`/recipe/${id}/copyRecipe`, { sede: state.sede });
            if (response.status === 200) {
                showToast(response.message);
                cancel();
                updateRecipe();
            } else {
                console.log('[RecipeCopyRecipe] copyRecipe error', response);
                helpers.failRequest(response, showToast, signOut);
            }
            showLoader(false);
        }
    }

    const cancel = () => {
        setState(prev => ({ ...prev, sede: defSede }));
        onClose();
    }

    return (
        <Collapse in={open}>
            <Divider />
            <h3>{'Copy a recipe'}</h3>
            <Grid container>
                <Grid item xs={6} md={3}>
                    <FormGroup>
                        <FormLabel>{'Sede to copy'}</FormLabel>
                        <Autocomplete
                            options={state.sedes}
                            value={state.sede}
                            getOptionLabel={option => option?.name ?? ''}
                            getOptionSelected={(option, value) => option.id === value.id}
                            onChange={handleChange}
                            renderInput={params => <TextField {...params} placeholder='Sede' />}
                        />
                    </FormGroup>
                </Grid>

                <Grid item xs={6} md={2} xl={1} className={classes.buttonContainer}>
                    <Button
                        variant='contained'
                        color='secondary'
                        style={{ width: '100%' }}
                        onClick={cancel}
                    >
                        {'Cancel'}
                    </Button>
                </Grid>
                {
                    (permissions.update_attr) &&
                    <Grid item xs={6} md={2} xl={1} className={classes.buttonContainer}>
                        <Button
                            variant='contained'
                            className={classes.storeBtn}
                            onClick={copyRecipe}
                        >
                            {'Copy a recipe'}
                        </Button>
                    </Grid>
                }
            </Grid>
        </Collapse>
    );
}

export default RecipeCopyRecipe;